/**
 * Formula Expression AST types + error classes (DYN-4.1).
 *
 * Extracted from `formulaParser.ts` to keep the parser module under
 * the CLAUDE.md 500/1000 LOC soft limits while still exposing the
 * full AST surface for consumers (evaluator, dependency walker,
 * BTABLE dialog).
 */

// ═══════════════════════════════════════════════════════════════════
// AST types — discriminated union on `kind`
// ═══════════════════════════════════════════════════════════════════

export type FormulaValue = number | string | boolean;

export interface NumberLiteralNode {
  readonly kind: "number";
  readonly value: number;
}

export interface StringLiteralNode {
  readonly kind: "string";
  readonly value: string;
}

export interface BooleanLiteralNode {
  readonly kind: "boolean";
  readonly value: boolean;
}

export interface VariableNode {
  readonly kind: "variable";
  /** Dotted path ("Width" | "Math.PI"). */
  readonly path: readonly string[];
}

export interface UnaryOpNode {
  readonly kind: "unary";
  readonly op: "-" | "+" | "!";
  readonly operand: FormulaAST;
}

export type BinaryOp =
  | "+"
  | "-"
  | "*"
  | "/"
  | "%"
  | "**"
  | "=="
  | "!="
  | "<"
  | ">"
  | "<="
  | ">="
  | "&&"
  | "||";

export interface BinaryOpNode {
  readonly kind: "binary";
  readonly op: BinaryOp;
  readonly left: FormulaAST;
  readonly right: FormulaAST;
}

export interface TernaryNode {
  readonly kind: "ternary";
  readonly condition: FormulaAST;
  readonly then: FormulaAST;
  readonly else: FormulaAST;
}

export interface FunctionCallNode {
  readonly kind: "call";
  /** Dotted name of the function (e.g. ["Math","sin"]). */
  readonly name: readonly string[];
  readonly args: readonly FormulaAST[];
}

export type FormulaAST =
  | NumberLiteralNode
  | StringLiteralNode
  | BooleanLiteralNode
  | VariableNode
  | UnaryOpNode
  | BinaryOpNode
  | TernaryNode
  | FunctionCallNode;

// ═══════════════════════════════════════════════════════════════════
// Errors
// ═══════════════════════════════════════════════════════════════════

export class FormulaParseError extends Error {
  readonly position: number;
  constructor(message: string, position: number) {
    super(message);
    this.name = "FormulaParseError";
    this.position = position;
  }
}
